import { useEffect } from 'react';
import { Common, color } from '@stockbit/ui';
import { Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';

import { FreezeLayer, OrderButton } from 'global/TradingModal/styled';
import If from 'global/ErrorHandlers/If';

import { BondTransactionActionEnum } from 'global/Securities/types/api/bond';
import { formatCurrency } from 'features/securities/withdrawal/utils';
import {
  BASE_UNIT_AMOUNT,
  BOND_TRANSACTION_CONFIG,
  BondInfoType,
  BondsModalTypeEnum,
} from '../../constants';
import { useCashOnHandStore } from '../../stores/useCashOnHandStore';
import { useBondsModalStore } from '../../stores/useBondsModalStore';
import { useBondTransaction } from '../../stores/useBondTransaction';
import useBondPreviewForm from '../../hooks/useBondPreviewForm';
import useBondsAnalytics from '../../useBondsAnalytics';
import {
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  UnitInput,
} from '../../styled';

import FormDisplayField from '../FormDisplayField';
import ModalProductDisplay from '../ModalProductDisplay';
import ModalTitle from '../ModalTitle';
import Accordion from '../Accordion';

import DepositAlert from './component/DepositAlert';
import { useBondInfoModal } from '../../stores/useBondInfoModal';

const { Text } = Common;

export default function ModalBondsBuy() {
  const [form] = useForm();

  const productId = useBondTransaction((s) => s.productId);
  const [
    transactionType,
    openConfirmModal,
    isSearchActive,
    toggleSearch,
    closeTransactionModal,
    sourcePage,
  ] = useBondsModalStore((s) => [
    s.transactionType,
    s.openConfirmModal,
    s.isSearchActive,
    s.toggleSearch,
    s.closeTransactionModal,
    s.sourcePage,
  ]);

  const openInfoModal = useBondInfoModal((s) => s.openInfoModal);

  const [cashOnHand, isLoadingCash] = useCashOnHandStore((s) => [
    s.cashOnHand,
    s.isLoading,
  ]);

  const {
    handleUnitAmountChanged,
    preview,
    unit,
    isLoadingPreview,
    isDisabled: previewDisabled,
  } = useBondPreviewForm({
    action: BondTransactionActionEnum.BUY,
    productId,
  });

  const { FRBondsNavigateTrackers, FRBondActionTrackers } = useBondsAnalytics();

  const { textColor, title } = BOND_TRANSACTION_CONFIG[transactionType];
  const isExceedAmount = preview?.buy?.total_amount > cashOnHand;
  const isDisabled =
    isLoadingCash || previewDisabled || isExceedAmount || isSearchActive;

  const handleSubmit = () => {
    FRBondActionTrackers.onOrderPlaced('Buy', productId);
    openConfirmModal();
  };

  useEffect(() => {
    if (productId) {
      FRBondsNavigateTrackers.onBondOrderPageViewed(
        'Buy',
        productId,
        sourcePage,
      );
    }
  }, []);

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      colon={false}
      labelCol={{ span: 10 }}
      wrapperCol={{ span: 8, offset: 2 }}
    >
      <ModalContainer modalType={BondsModalTypeEnum.TRANSACTION}>
        <If condition={isSearchActive}>
          <FreezeLayer onClick={() => toggleSearch(false)} />
        </If>
        <ModalHeader>
          <ModalTitle
            color={textColor}
            title={title}
            onClose={closeTransactionModal}
          />
          <ModalProductDisplay isSearchEnabled />
        </ModalHeader>
        <ModalBody>
          <If condition={isExceedAmount}>
            <DepositAlert />
          </If>
          <FormDisplayField
            label="Cash On Hand"
            isLoading={isLoadingCash}
            value={Math.round(cashOnHand)}
            type="currency"
            isNeedInfo
            isBold
            onInfoClick={() => openInfoModal(BondInfoType.CASH_ON_HAND)}
          />
          <Form.Item label="Unit Pembelian">
            <UnitInput
              value={unit}
              onChange={handleUnitAmountChanged}
              placeholder={`1 Unit = ${formatCurrency(
                BASE_UNIT_AMOUNT.toString(),
              )}`}
              type="number"
            />
          </Form.Item>
          <FormDisplayField
            label="Harga Beli (%)"
            value={preview?.buy?.price_rate}
            type="percentage"
            onInfoClick={() => openInfoModal(BondInfoType.BUYING_PRICE)}
            isNeedInfo
            isDisabled
          />

          <Accordion
            title={
              <FormDisplayField
                label="Total Pembayaran"
                isLoading={isLoadingPreview}
                value={preview?.buy?.total_amount}
                isBold
                type="currency"
                defaultColor={isExceedAmount ? color.sunsetOrange : '#333333'}
              />
            }
          >
            <FormDisplayField
              label="Nilai Pembelian"
              isLoading={isLoadingPreview}
              value={preview?.buy?.buy_price_amount}
              type="currency"
            />
            <FormDisplayField
              label="Kupon Penjual"
              isLoading={isLoadingPreview}
              value={preview?.buy?.accrued_interest}
              type="currency"
              isNeedInfo
              onInfoClick={() =>
                openInfoModal(BondInfoType.ACCRUED_INTEREST_SELLER)
              }
              isShowSign={preview?.buy?.accrued_interest > 0}
            />
            <FormDisplayField
              label="Bea Meterai"
              isLoading={isLoadingPreview}
              value={preview?.buy?.fee?.stamp_duty}
              type="currency"
              isShowSign={preview?.buy?.fee?.stamp_duty > 0}
            />
          </Accordion>
        </ModalBody>
      </ModalContainer>
      <ModalFooter>
        <OrderButton
          disabled={isDisabled}
          type="primary"
          htmlType="submit"
          block
        >
          <Text>Place Order</Text>
        </OrderButton>
      </ModalFooter>
    </Form>
  );
}
